import { useEffect } from 'react'
import { Box, Typography, Container, useTheme } from '@mui/material'
import contentData from '../../Assets/JSON/BulletSectionsList/BulletsectionsList.json'
import { motion, useAnimation } from 'framer-motion'
import { useInView } from 'react-intersection-observer'

const AnimatedSection = ({ section }) => {
  const controls = useAnimation()
  const theme = useTheme()
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.2 })

  useEffect(() => {
    if (inView) controls.start('visible')
  }, [controls, inView])

  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={controls}
      variants={{
        hidden: { opacity: 0, y: 50 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.8 } },
      }}
    >
      <Box sx={{ mb: 6 }}>
        {/* Heading */}
        <Box sx={{ position: 'relative', display: 'block', mb: 4 }}>
          <Typography
            variant="h4"
            component="h2"
            sx={{ color: theme.palette.text.main, mb: 1 }}
          >
            {section.heading}
          </Typography>
          <Box
            sx={{
              width: '100%',
              height: 5,
              backgroundColor: theme.palette.primary.main,
              position: 'absolute',
            }}
          />
        </Box>

        {/* Dynamically Render Sub Blocks */}
        {Object.entries(section).map(([key, value]) => {
          if (key.startsWith('subheading')) {
            return (
              <Typography
                key={key}
                variant="h6"
                sx={{
                  color: theme.palette.text.secondary,
                  mb: 2,
                }}
              >
                {value}
              </Typography>
            )
          }
          if (key.startsWith('description')) {
            return (
              <Typography
                key={key}
                variant="body2"
                sx={{
                  color: theme.palette.text.paragraph,
                  mb: 2,
                }}
              >
                {value}
              </Typography>
            )
          }
          if (key.startsWith('subtitle')) {
            return (
              <Typography
                key={key}
                variant="body1"
                sx={{
                  fontWeight: 500,
                  fontSize: '1rem',
                  mt: 1,
                  color: theme.palette.text.primary,
                }}
              >
                {value}
              </Typography>
            )
          }

          if (key.startsWith('items') && Array.isArray(value)) {
            return (
              <Box key={key} component="ul" sx={{ pl: 3, mt: 1 }}>
                {value.map((item, index) => (
                  <Box
                    key={`${key}-item-${index}`}
                    component="li"
                    sx={{
                      mb: 0.5,
                      color: theme.palette.text.paragraph,
                      fontSize: '0.9rem',
                      lineHeight: 1.5,
                    }}
                  >
                    {item}
                  </Box>
                ))}
              </Box>
            )
          }

          return null
        })}
      </Box>
    </motion.div>
  )
}

const BulletContentSection = ({ pageId }) => {
  const theme = useTheme()
  const pageContent = contentData[pageId]

  if (!pageContent) {
    return (
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Typography variant="h4">Content not found</Typography>
      </Container>
    )
  }

  return (
    <>
      {pageContent.bannerImage && (
        <Box
          sx={{
            width: '100%',
            mb: 4,
            height: { md: '80vh' },
            position: 'relative',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: theme.palette.grey[200],
            aspectRatio: { xs: '4/3', md: '16/9' },
            overflow: 'hidden',
          }}
        >
          <Box
            component="img"
            src={pageContent.bannerImage}
            alt={pageContent.bannerText || 'Banner Image'}
            loading="lazy"
            sx={{
              width: '100%',
              height: 'auto',
              objectFit: 'cover',
              position: 'absolute',
              top: 0,
              left: 0,
              zIndex: 1,
              minHeight: '100%',
            }}
          />

          <Box
            sx={{
              position: 'relative',
              zIndex: 2,
              width: '100%',
              height: '100%',
              background:
                'linear-gradient(to top, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.3) 100%)',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'flex-end',
              alignItems: 'flex-start',
              color: 'white',
              textAlign: 'start',
              p: 3,
            }}
          >
            <Box
              sx={{
                backgroundColor: theme.palette.background.primaryBackground,
                borderRadius: 1,
              }}
            >
              {pageContent.bannerText && (
                <Typography
                  variant="h4"
                  component="h5"
                  sx={{
                    textShadow: '0 2px 8px rgba(0,0,0,0.7)',
                    padding: 2,
                    fontWeight: 'bold',
                  }}
                >
                  {pageContent.bannerText}
                </Typography>
              )}
              {pageContent.bannerSubtext && (
                <Typography
                  variant="h5"
                  component="div"
                  sx={{
                    textShadow: '0 1px 4px rgba(0,0,0,0.5)',
                    fontWeight: 'normal',
                    fontSize: { xs: '1.2rem', sm: '1.5rem' },
                    maxWidth: '80%',
                    mt: 2,
                  }}
                >
                  {pageContent.bannerSubtext}
                </Typography>
              )}
            </Box>
          </Box>
        </Box>
      )}
      <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3 } }}>
        <Box sx={{ padding: { xs: '1rem', sm: '2rem' } }}>
          {pageContent.sections.map((section, index) => (
            <AnimatedSection key={index} section={section} />
          ))}
          {pageContent.contact && (
            <Box sx={{ mt: 4, textAlign: 'center' }}>
              <Typography
                variant="h6"
                sx={{
                  color: theme.palette.text.primary,
                  fontWeight: 'bold',
                  mb: 1,
                }}
              >
                {pageContent.contact.title || 'Contact Information'}
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: theme.palette.primary.main,
                  fontSize: '1.1rem',
                  '&:hover': {
                    textDecoration: 'underline',
                    cursor: 'pointer',
                  },
                }}
                component="a"
                href={`mailto:${pageContent.contact.email}`}
              >
                {pageContent.contact.email}
              </Typography>
              {pageContent.contact.phone && (
                <Typography
                  variant="body1"
                  sx={{
                    color: theme.palette.primary.main,
                    fontSize: '1.1rem',
                    mt: 1,
                  }}
                >
                  {pageContent.contact.phone}
                </Typography>
              )}
            </Box>
          )}
        </Box>
      </Container>
    </>
  )
}

export default BulletContentSection
